/* ------------------------------------------------------------------------------
 *
 *  # Student Works Gallery Management - Galería de Trabajos
 *
 *  Gestión completa de galería con upload, asignación y visualización
 *
 * ---------------------------------------------------------------------------- */

// ============================================================================
// VARIABLES GLOBALES Y ESTADO
// ============================================================================
import {
	initGenericDataTable,
	refreshDataTable,
	destroyDataTable
} from '../integrations/datatables.js';

let galeriaCallbacks = null;
let fotosEnMemoria = []; // Array de archivos en memoria
let currentAmbienteId = 0;
let currentUsuarioId = 0;
// ============================================================================
// FUNCIÓN PRINCIPAL DE INICIALIZACIÓN
// ============================================================================
export function initGaleria() {
	console.log('Inicializando módulo de galería...');

	// Esperar un momento para asegurar que el DOM esté listo
	setTimeout(() => {
		// Inicializar file input si existe
		const fileInput = document.getElementById('galeriaFileInput');
		if (fileInput) {
			initFileInput();
		} else {
			console.warn('Elemento galeriaFileInput no encontrado en el DOM');
		}

		// Event listeners
		setupEventListeners();
	}, 100);
}

// ============================================================================
// CALLBACKS DESDE BLAZOR
// ============================================================================
export function setCallbacks(dotNetRef) {
	galeriaCallbacks = dotNetRef;
	console.log('Callbacks configurados para galería');
}

// ============================================================================
// FILE INPUT (Bootstrap FileInput con configuración completa)
// ============================================================================
function initFileInput() {
if (!$().fileinput) {
console.warn('Warning - fileinput.min.js is not loaded.');
return;
}

// ✅ Usar el mismo selector (por ID para ser consistente)
const $fileInput = $('#galeriaFileInput');
	
if ($fileInput.length === 0) {
console.warn('No se encontró el elemento #galeriaFileInput');
return;
}

// ✅ DESTRUIR el plugin si ya estaba inicializado
if ($fileInput.data('fileinput')) {
console.log('Destruyendo fileinput previo...');
try {
$fileInput.fileinput('destroy');
} catch (e) {
console.warn('Error al destruir fileinput:', e);
}
		
// Limpiar clases y atributos residuales
$fileInput.removeClass('file-input-galeria fileinput-exists');
$fileInput.next('.file-caption').remove();
}

// ✅ LIMPIAR EVENT HANDLERS PREVIOS
$fileInput.off('filebatchselected fileclear');

	// Buttons inside zoom modal
	const previewZoomButtonClasses = {
		rotate: 'btn btn-light btn-icon btn-sm',
		toggleheader: 'btn btn-light btn-icon btn-header-toggle btn-sm',
		fullscreen: 'btn btn-light btn-icon btn-sm',
		borderless: 'btn btn-light btn-icon btn-sm',
		close: 'btn btn-light btn-icon btn-sm'
	};

	// Icons inside zoom modal classes
	const previewZoomButtonIcons = {
		prev: document.dir == 'rtl' ? '<i class="ph-arrow-right"></i>' : '<i class="ph-arrow-left"></i>',
		next: document.dir == 'rtl' ? '<i class="ph-arrow-left"></i>' : '<i class="ph-arrow-right"></i>',
		rotate: '<i class="ph-arrow-clockwise"></i>',
		toggleheader: '<i class="ph-arrows-down-up"></i>',
		fullscreen: '<i class="ph-corners-out"></i>',
		borderless: '<i class="ph-frame-corners"></i>',
		close: '<i class="ph-x"></i>'
	};

	// File actions
	const fileActionSettings = {
		zoomClass: '',
		zoomIcon: '<i class="ph-magnifying-glass-plus"></i>',
		dragClass: 'p-2',
		dragIcon: '<i class="ph-dots-six"></i>',
		removeClass: '',
		removeErrorClass: 'text-danger',
		removeIcon: '<i class="ph-trash"></i>',
		indicatorNew: '<i class="ph-file-plus text-success"></i>',
		indicatorSuccess: '<i class="ph-check file-icon-large text-success"></i>',
		indicatorError: '<i class="ph-x text-danger"></i>',
		indicatorLoading: '<i class="ph-spinner spinner text-muted"></i>'
	};


	$fileInput.fileinput({
		browseLabel: 'Seleccionar',
		browseIcon: '<i class="ph-file-plus me-2"></i>',
		uploadIcon: '<i class="ph-file-arrow-up me-2"></i>',
		removeIcon: '<i class="ph-x fs-base me-2"></i>',
		layoutTemplates: {
			icon: '<i class="ph-check"></i>'
		},
		uploadClass: 'btn btn-light',
		removeClass: 'btn btn-light',
		initialCaption: "Ningún archivo seleccionado",
		showUpload: false,  // ✅ SIN BOTÓN UPLOAD
		showRemove: true,
		dropZoneEnabled: true, // ✅ HABILITAR Drag & Drop
		dropZoneTitle: 'Arrastra archivos aquí o haz clic para seleccionar',
		msgPlaceholder: 'Selecciona archivos de imagen...',
		allowedFileExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
		maxFileCount: 50,
		previewZoomButtonClasses: previewZoomButtonClasses,
		previewZoomButtonIcons: previewZoomButtonIcons,
		fileActionSettings: fileActionSettings
	});

	// ✅ EVENTO CORRECTO: filebatchselected (funciona con botón Y drag & drop)
	$fileInput.on('filebatchselected', function (event, files) {
		console.log('Archivos seleccionados via filebatchselected:', files.length);
		if (files && files.length > 0) {
			handleFilesSelected(files);
		}
	});

	// Al limpiar
	$fileInput.on('fileclear', function () {
		limpiarFotosMemoria();
	});
}

// ============================================================================
// GESTIÓN DE ARCHIVOS EN MEMORIA
// ============================================================================
async function handleFilesSelected(files) {
	console.log(`${files.length} archivos seleccionados`);

	fotosEnMemoria = [];
	let totalSize = 0;
	const MAX_SIZE = 100 * 1024 * 1024; // 100MB

	for (let i = 0; i < files.length; i++) {
		const file = files[i];

		if (!file.type.startsWith('image/')) {
			console.warn(`Archivo ${file.name} no es una imagen válida`);
			continue;
		}

		totalSize += file.size;
		if (totalSize > MAX_SIZE) {
			window.showNotification(`Tamaño total: ${formatFileSize(totalSize)}. Máximo 100MB. Reduce imágenes.`, 'error');
			fotosEnMemoria = [];
			renderFotosTable();
			return;
		}

		try {
			const base64 = await fileToBase64(file);
			fotosEnMemoria.push({
				nombre: file.name,
				tamaño: file.size,
				tipo: file.type,
				base64Data: base64,
				file: file,
				seleccionada: false
			});
		} catch (error) {
			console.error(`Error procesando ${file.name}:`, error);
		}
	}

	console.log(`${fotosEnMemoria.length} fotos (${formatFileSize(totalSize)} total)`);
	renderFotosTable();
}

// Convertir archivo a Base64
function fileToBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			// Eliminar el prefijo "data:image/...;base64,"
			const base64 = reader.result.split(',')[1];
			resolve(base64);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}

// ============================================================================
// DATATABLE DE FOTOS PENDIENTES
// ============================================================================
function renderFotosTable() {
	const tbody = document.getElementById('fotosTableBody');
	const card = document.getElementById('fotosCard');
	const btnAsignarEst = document.getElementById('btnAsignarEstudiantes');
	const btnAsignarTrab = document.getElementById('btnAsignarTrabajo');

	if (!tbody || !card) return;

	// Mostrar card si hay fotos
	if (fotosEnMemoria.length > 0) {
		card.style.display = 'block';

		// Limpiar tbody
		tbody.innerHTML = '';

		// Renderizar cada foto
		fotosEnMemoria.forEach((foto, index) => {
			const tr = document.createElement('tr');
			tr.innerHTML = `
				<td>
					<input type="checkbox" 
					       class="form-check-input foto-checkbox" 
					       data-index="${index}" 
					       ${foto.seleccionada ? 'checked' : ''}>
				</td>
				<td>
					<img src="data:${foto.tipo};base64,${foto.base64Data}" 
					     alt="${foto.nombre}" 
					     style="width: 50px; height: 50px; object-fit: cover;" 
					     class="rounded">
				</td>
				<td>${foto.nombre}</td>
				<td>${formatFileSize(foto.tamaño)}</td>
				<td>
					<a href="javascript:void(0)" 
					   class="text-danger" 
					   onclick="window.sfBlazorSB.students.works.eliminarFoto(${index})"
					   title="Eliminar">
						<i class="ph-trash"></i>
					</a>
				</td>
			`;
			tbody.appendChild(tr);
		});

		// Event listeners para checkboxes
		setupCheckboxListeners();

		// Habilitar/deshabilitar botones según selección
		actualizarEstadoBotones();
	} else {
		card.style.display = 'none';
	}
}

function setupCheckboxListeners() {
	// Checkbox "Seleccionar todas"
	const checkAll = document.getElementById('checkAllFotos');
	if (checkAll) {
		checkAll.addEventListener('change', function () {
			const checkboxes = document.querySelectorAll('.foto-checkbox');
			checkboxes.forEach((cb, index) => {
				cb.checked = checkAll.checked;
				fotosEnMemoria[index].seleccionada = checkAll.checked;
			});
			actualizarEstadoBotones();
		});
	}

	// Checkboxes individuales
	const checkboxes = document.querySelectorAll('.foto-checkbox');
	checkboxes.forEach((cb) => {
		cb.addEventListener('change', function () {
			const index = parseInt(this.dataset.index);
			fotosEnMemoria[index].seleccionada = this.checked;
			actualizarEstadoBotones();

			// Actualizar "seleccionar todas"
			const allChecked = fotosEnMemoria.every(f => f.seleccionada);
			if (checkAll) checkAll.checked = allChecked;
		});
	});
}

function actualizarEstadoBotones() {
	const btnAsignarEst = document.getElementById('btnAsignarEstudiantes');
	const btnAsignarAmb = document.getElementById('btnAsignarAmbientes');
	const btnAsignarTrab = document.getElementById('btnAsignarTrabajo');

	const haySeleccionadas = fotosEnMemoria.some(f => f.seleccionada);

	if (btnAsignarEst) btnAsignarEst.disabled = !haySeleccionadas;
	if (btnAsignarAmb) btnAsignarAmb.disabled = !haySeleccionadas;
	if (btnAsignarTrab) btnAsignarTrab.disabled = !haySeleccionadas;
}

export function eliminarFoto(index) {
	if (confirm('¿Eliminar esta foto?')) {
		fotosEnMemoria.splice(index, 1);
		renderFotosTable();
	}
}

function limpiarFotosMemoria() {
	fotosEnMemoria = [];
	renderFotosTable();
	console.log('Memoria de fotos limpiada');
}

// ============================================================================
// ABRIR MODALES
// ============================================================================
export function openModalEstudiantes(estudiantes, ambienteId) {
	currentAmbienteId = ambienteId;

	// Verificar que haya fotos seleccionadas
	const fotosSeleccionadas = fotosEnMemoria.filter(f => f.seleccionada);
	if (fotosSeleccionadas.length === 0) {
		window.showNotification('Selecciona al menos una foto', 'warning');
		return;
	}

	// Inicializar Select2 con estudiantes
	initSelect2Estudiantes(estudiantes, 'selectEstudiantes');

	// Inicializar Tokenfield para etiquetas
	initTokenfieldEtiquetas('etiquetasEstudiantes');

	// Limpiar campos
	document.getElementById('tituloEstudiantes').value = '';
	document.getElementById('descripcionEstudiantes').value = '';

	// Abrir modal
	$('#modalAsignarEstudiantes').modal('show');
}

export function openModalTrabajo(estudiantes, actividades, ambienteId) {
	currentAmbienteId = ambienteId;

	// Verificar que haya fotos seleccionadas
	const fotosSeleccionadas = fotosEnMemoria.filter(f => f.seleccionada);
	if (fotosSeleccionadas.length === 0) {
		window.showNotification('Selecciona al menos una foto', 'warning');
		return;
	}

	// Inicializar Select2 con estudiantes
	initSelect2Estudiantes(estudiantes, 'selectEstudiantesTrabajo');

	// Inicializar Select de actividades
	initSelectActividades(actividades);

	// Inicializar Tokenfield para etiquetas
	initTokenfieldEtiquetas('etiquetasTrabajo');

	// Limpiar campos
	document.getElementById('tituloTrabajo').value = '';
	document.getElementById('descripcionTrabajo').value = '';

	// Abrir modal
	$('#modalAsignarTrabajo').modal('show');
}

export function openModalAmbientes(ambientes) {
	// Verificar que haya fotos seleccionadas
	const fotosSeleccionadas = fotosEnMemoria.filter(f => f.seleccionada);
	if (fotosSeleccionadas.length === 0) {
		window.showNotification('Selecciona al menos una foto', 'warning');
		return;
	}

	// Inicializar Select2 con ambientes
	initSelect2Ambientes(ambientes, 'selectAmbientes');

	// Inicializar Tokenfield para etiquetas
	initTokenfieldEtiquetas('etiquetasAmbientes');

	// Limpiar campos
	document.getElementById('tituloAmbientes').value = '';
	document.getElementById('descripcionAmbientes').value = '';

	// Abrir modal
	$('#modalAsignarAmbientes').modal('show');
}

// ============================================================================
// SELECT2 PARA ESTUDIANTES
// ============================================================================
function initSelect2Estudiantes(estudiantes, selectId) {
	const $select = $(`#${selectId}`);

	// Destruir select2 previo si existe
	if ($select.hasClass('select2-hidden-accessible')) {
		$select.select2('destroy');
	}

	// Preparar datos
	const data = estudiantes.map(e => ({
		id: e.id,
		text: `${e.fullName}`.trim()
	}));

	// Inicializar Select2
	$select.select2({
		data: data,
		placeholder: 'Selecciona estudiantes',
		allowClear: true,
		multiple: true,
		dropdownParent: $select.closest('.modal')
	});
}

// ============================================================================
// SELECT2 PARA ACTIVIDADES
// ============================================================================
function initSelectActividades(actividades) {
	const $select = $('#selectActividad');
	if (!$select.length) return;

	if ($select.hasClass('select2-hidden-accessible')) {
		$select.select2('destroy');
	}

	const data = [
		{ id: '', text: 'Sin actividad específica' },
		...actividades.map(act => ({ id: act.id, text: act.nombre }))
	];

	$select.select2({
		data: data,
		placeholder: 'Selecciona una actividad',
		allowClear: true,
		dropdownParent: $select.closest('.modal')
	});
}

// ============================================================================
// TOKENFIELD PARA ETIQUETAS
// ============================================================================
function initTokenfieldEtiquetas(inputId) {
	if (typeof Tokenfield === 'undefined') {
		console.warn('Tokenfield not loaded, using simple input');
		return;
	}

	const input = document.getElementById(inputId);
	if (!input) return;

	// Etiquetas predefinidas (SIN publica/privada - se maneja automáticamente)
	const etiquetasPredefinidas = [
		{ id: 1, name: 'ambiente' },
		{ id: 2, name: 'exterior' },
		{ id: 3, name: 'interior' },
		{ id: 4, name: 'patio' },
		{ id: 5, name: 'comedor' }
	];

	// Inicializar Tokenfield
	new Tokenfield({
		el: input,
		items: etiquetasPredefinidas,
		newItems: false,
		minChars: 1,
		multiple: true,
		delimiters: [',', ';'],
		placeholder: 'Escribe etiquetas...'
	});
}

// ============================================================================
// OBTENER DATOS DE MODALES PARA ENVIAR A BLAZOR
// ============================================================================
export function getDataAsignacionEstudiantes(usuarioId) {

	currentUsuarioId = usuarioId;

	// Obtener estudiantes seleccionados
	const estudiantesIds = $('#selectEstudiantes').val() || [];

	if (estudiantesIds.length === 0) {
		window.showNotification('Selecciona al menos un estudiante', 'warning');
		return null;
	}

	// Obtener datos del formulario
	const titulo = document.getElementById('tituloEstudiantes').value.trim();
	const descripcion = document.getElementById('descripcionEstudiantes').value.trim();

	// Obtener etiquetas desde Tokenfield (opcional)
	const etiquetasElement = document.getElementById('etiquetasEstudiantes');
	const tokenfield = etiquetasElement?.tokenfield;
	const etiquetasArray = tokenfield ? tokenfield.getItems().map(item => item.name) : [];
	const etiquetasInput = etiquetasArray.join(',');

	// Validaciones de campos requeridos
	if (!titulo) {
		window.showNotification('El título es requerido', 'warning');
		return null;
	}

	// Obtener fotos seleccionadas
	const fotosSeleccionadas = fotosEnMemoria
		.filter(f => f.seleccionada)
		.map(f => ({
			nombreArchivo: f.nombre,
			base64Data: f.base64Data,
			tamañoBytes: f.tamaño,
			subidaPorUsuarioId: usuarioId
		}));

	if (fotosSeleccionadas.length === 0) {
		window.showNotification('No hay fotos seleccionadas', 'warning');
		return null;
	}

	// Construir request
	return {
		estudiantesIds: estudiantesIds.map(id => parseInt(id)),
		actividadId: null,
		titulo: titulo,
		descripcion: descripcion,
		etiquetas: etiquetasInput, // Las etiquetas del usuario (se añadirá "privada" en el servidor)
		ambienteId: currentAmbienteId,
		imagenes: fotosSeleccionadas
	};
}

export function getDataAsignacionTrabajo(usuarioId) {
	currentUsuarioId = usuarioId;

	// Obtener estudiantes seleccionados
	const estudiantesIds = $('#selectEstudiantesTrabajo').val() || [];

	if (estudiantesIds.length === 0) {
		window.showNotification('Selecciona al menos un estudiante', 'warning');
		return null;
	}

	// Obtener actividad
	const actividadId = document.getElementById('selectActividad').value;

	// Obtener datos del formulario
	const titulo = document.getElementById('tituloTrabajo').value.trim();
	const descripcion = document.getElementById('descripcionTrabajo').value.trim();

	// ✅ Obtener etiquetas desde la instancia de Tokenfield
	const etiquetasElement = document.getElementById('etiquetasTrabajo');
	const tokenfield = etiquetasElement?.tokenfield;
	const etiquetasArray = tokenfield ? tokenfield.getItems().map(item => item.name) : [];
	const etiquetasInput = etiquetasArray.join(',');

	// Validaciones de campos requeridos
	if (!titulo) {
		window.showNotification('El título es requerido', 'warning');
		return null;
	}

	// Obtener fotos seleccionadas
	const fotosSeleccionadas = fotosEnMemoria
		.filter(f => f.seleccionada)
		.map(f => ({
			nombreArchivo: f.nombre,
			base64Data: f.base64Data,
			tamañoBytes: f.tamaño,
			subidaPorUsuarioId: usuarioId
		}));

	if (fotosSeleccionadas.length === 0) {
		window.showNotification('No hay fotos seleccionadas', 'warning');
		return null;
	}

	// Construir request
	return {
		estudiantesIds: estudiantesIds.map(id => parseInt(id)),
		actividadId: actividadId ? parseInt(actividadId) : null,
		titulo: titulo,
		descripcion: descripcion,
		etiquetas: etiquetasInput,
		ambienteId: currentAmbienteId,
		imagenes: fotosSeleccionadas
	};
}

// ============================================================================
// LIGHTBOX (Galería de imágenes con GLightbox)
// ============================================================================
export function initLightbox() {
	if (typeof GLightbox === 'undefined') {
		console.warn('Warning - glightbox.min.js is not loaded.');
		return;
	}

	// Inicializar GLightbox para galería de trabajos
	const lightbox = GLightbox({
		selector: '[data-bs-popup="lightbox"]',
		loop: true,
		svg: {
			next: document.dir == "rtl" ? '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 477.175 477.175" xml:space="preserve"><g><path d="M145.188,238.575l215.5-215.5c5.3-5.3,5.3-13.8,0-19.1s-13.8-5.3-19.1,0l-225.1,225.1c-5.3,5.3-5.3,13.8,0,19.1l225.1,225c2.6,2.6,6.1,4,9.5,4s6.9-1.3,9.5-4c5.3-5.3,5.3-13.8,0-19.1L145.188,238.575z"/></g></svg>' : '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 477.175 477.175" xml:space="preserve"> <g><path d="M360.731,229.075l-225.1-225.1c-5.3-5.3-13.8-5.3-19.1,0s-5.3,13.8,0,19.1l215.5,215.5l-215.5,215.5c-5.3,5.3-5.3,13.8,0,19.1c2.6,2.6,6.1,4,9.5,4c3.4,0,6.9-1.3,9.5-4l225.1-225.1C365.931,242.875,365.931,234.275,360.731,229.075z"/></g></svg>',
			prev: document.dir == "rtl" ? '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 477.175 477.175" xml:space="preserve"><g><path d="M360.731,229.075l-225.1-225.1c-5.3-5.3-13.8-5.3-19.1,0s-5.3,13.8,0,19.1l215.5,215.5l-215.5,215.5c-5.3,5.3-5.3,13.8,0,19.1c2.6,2.6,6.1,4,9.5,4c3.4,0,6.9-1.3,9.5-4l225.1-225.1C365.931,242.875,365.931,234.275,360.731,229.075z"/></g></svg>' : '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 477.175 477.175" xml:space="preserve"><g><path d="M145.188,238.575l215.5-215.5c5.3-5.3,5.3-13.8,0-19.1s-13.8-5.3-19.1,0l-225.1,225.1c-5.3,5.3-5.3,13.8,0,19.1l225.1,225c2.6,2.6,6.1,4,9.5,4s6.9-1.3,9.5-4c5.3-5.3,5.3-13.8,0-19.1L145.188,238.575z"/></g></svg>'
		}
	});
}

// ============================================================================
// TABLE PARA VISUALIZACIÓN DESDE PERIL ESTUDIANTE.
// ============================================================================

export function initStudentWorksGallery(trabajos) {
	const galleryContainer = document.getElementById('worksGalleryContainer') || document.getElementById('galeriaContainer');
	if (!galleryContainer) {
		console.warn('Gallery container not found');
		return;
	}

	// Clear existing content
	galleryContainer.innerHTML = '';

	// Generate gallery items
	trabajos.forEach(trabajo => {
		if (trabajo.imagenes && trabajo.imagenes.length > 0) {
			trabajo.imagenes.forEach(imagen => {
				const galleryItem = createGalleryItem(trabajo, imagen);
				galleryContainer.appendChild(galleryItem);
			});
		}
	});

	// Initialize lightbox
	initLightbox();
}

export function refreshStudentWorksGallery(trabajos) {
	initStudentWorksGallery(trabajos);
}

export function initStudentWorksTable(trabajos) {
	destroyDataTable('studentWorksTable');

	const tableData = trabajos.map(trabajo => [
		generateWorkActions(trabajo.id),
		generateWorkColumn(trabajo),
		generateImagesColumn(trabajo),
		generateTagsColumn(trabajo),
		new Date(trabajo.fechaCreacion).toLocaleDateString('es-ES')
	]);

	const options = {
		data: tableData,
		columns: [
			{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '100px' },
			{ title: 'Trabajo', width: '35%' },
			{ title: 'Imágenes', className: 'text-center', width: '15%' },
			{ title: 'Etiquetas', width: '30%' },
			{ title: 'Fecha', className: 'text-center', width: '20%' }
		],
		exportTitle: 'Trabajos de Estudiante - Montessori Nova',
		order: [[4, 'desc']]
	};

	return initGenericDataTable('studentWorksTable', options);
}

function generateWorkActions(trabajoId) {
	return `
		<div class="d-inline-flex">
			<a href="javascript:void(0)" class="text-body me-2" 
			   onclick="window.sfBlazorSB.students.works.viewImages(${trabajoId})" 
			   title="Ver imágenes">
				<i class="ph-image"></i>
			</a>
		</div>
	`;
}

function generateWorkColumn(trabajo) {
	const exportText = trabajo.titulo;

	return `
		<div data-export="${exportText}">
			<div class="fw-semibold">${trabajo.titulo}</div>
			${trabajo.descripcion ? `<div class="text-muted fs-sm mt-1">${trabajo.descripcion}</div>` : ''}
			${trabajo.actividadNombre ? `<div class="text-muted fs-sm mt-1"><i class="ph-link ph-xs me-1"></i>${trabajo.actividadNombre}</div>` : ''}
		</div>
	`;
}

function generateImagesColumn(trabajo) {
	const count = trabajo.imagenes ? trabajo.imagenes.length : 0;
	return `
		<span class="badge bg-primary" data-export="${count}">
			<i class="ph-image me-1"></i>${count}
		</span>
	`;
}

function generateTagsColumn(trabajo) {
	if (!trabajo.etiquetas) {
		return '<span class="text-muted" data-export="-">Sin etiquetas</span>';
	}

	const tags = trabajo.etiquetas.split(',').map(t => t.trim());
	const exportText = tags.join(', ');
	const badges = tags.map(tag =>
		`<span class="badge ${getTagBadgeClass(tag)} me-1">${tag}</span>`
	).join('');

	return `<div data-export="${exportText}">${badges}</div>`;
}

function getTagBadgeClass(tag) {
	const tagColors = {
		'publica': 'bg-primary',
		'privada': 'bg-success',
		'ambiente': 'bg-warning',
		'exterior': 'bg-info',
		'interior': 'bg-indigo',
		'patio': 'bg-pink',
		'comedor': 'bg-dark'
	};
	return tagColors[tag.toLowerCase()] || 'bg-secondary';
}

export function refreshStudentWorksTable(trabajos) {
	destroyDataTable('studentWorksTable');
	initStudentWorksTable(trabajos);
}

export function viewImages(trabajoId) {
	if (galeriaCallbacks) {
		galeriaCallbacks.invokeMethodAsync('ViewWorkImages', trabajoId);
	}
}

// ============================================================================
// FILTROS DE GALERÍA
// ============================================================================
export function applyWorkFilters() {
	const tagFilter = document.getElementById('tagFilter');
	const fechaDesde = document.getElementById('fechaDesde');
	const fechaHasta = document.getElementById('fechaHasta');

	const selectedTag = tagFilter ? tagFilter.value.toLowerCase().trim() : '';
	const dateFrom = fechaDesde ? fechaDesde.value : '';
	const dateTo = fechaHasta ? fechaHasta.value : '';

	console.log('Aplicando filtros:', { selectedTag, dateFrom, dateTo });

	// Obtener todos los items de la galería
	const galleryItems = document.querySelectorAll('#worksGalleryContainer > div.col-sm-6, #galeriaContainer > div.col-sm-6, #galeriaContainer > div.col-lg-4');

	galleryItems.forEach(item => {
		let shouldShow = true;

		// Filtro por tag
		if (selectedTag) {
			const badges = item.querySelectorAll('.badge');
			const itemTags = Array.from(badges)
				.map(badge => badge.textContent.trim().toLowerCase())
				.filter(text => text && !text.includes('ph-image')); // Excluir badge de cantidad
			
			shouldShow = itemTags.some(tag => tag.includes(selectedTag));
		}

		// Filtro por fecha
		if (shouldShow && (dateFrom || dateTo)) {
			const dateElement = item.querySelector('.ph-calendar');
			if (dateElement) {
				const dateText = dateElement.closest('div').textContent.trim();
				// Convertir formato dd/MM/yyyy a objeto Date
				const parts = dateText.split('/');
				if (parts.length === 3) {
					const itemDate = new Date(parts[2], parts[1] - 1, parts[0]);
					
					if (dateFrom) {
						const fromDate = new Date(dateFrom);
						if (itemDate < fromDate) shouldShow = false;
					}
					
					if (dateTo && shouldShow) {
						const toDate = new Date(dateTo);
						if (itemDate > toDate) shouldShow = false;
					}
				}
			}
		}

		// Mostrar u ocultar item
		item.style.display = shouldShow ? 'block' : 'none';
	});

	// Mostrar mensaje si no hay resultados
	const visibleItems = Array.from(galleryItems).filter(item => item.style.display !== 'none');
	const container = document.getElementById('worksGalleryContainer') || document.getElementById('galeriaContainer');
	
	if (visibleItems.length === 0 && container) {
		const noResults = container.querySelector('.no-results-message');
		if (!noResults) {
			const message = document.createElement('div');
			message.className = 'col-12 no-results-message';
			message.innerHTML = `
				<div class="text-center text-muted py-5">
					<i class="ph-magnifying-glass ph-3x mb-3"></i>
					<p>No se encontraron trabajos con los filtros aplicados</p>
				</div>
			`;
			container.appendChild(message);
		}
	} else {
		const noResults = container?.querySelector('.no-results-message');
		if (noResults) noResults.remove();
	}
}

export function clearWorkFilters() {
	const tagFilter = document.getElementById('tagFilter');
	const fechaDesde = document.getElementById('fechaDesde');
	const fechaHasta = document.getElementById('fechaHasta');

	if (tagFilter) tagFilter.value = '';
	if (fechaDesde) fechaDesde.value = '';
	if (fechaHasta) fechaHasta.value = '';

	// Show all items
	const galleryItems = document.querySelectorAll('#worksGalleryContainer > div, #galeriaContainer > div');
	galleryItems.forEach(item => {
		item.style.display = 'block';
	});
}

// ============================================================================
// EVENT LISTENERS
// ============================================================================
function setupEventListeners() {
	// Aquí podrían ir listeners adicionales si es necesario
}

// ============================================================================
// HELPERS
// ============================================================================
function formatFileSize(bytes) {
	if (bytes === 0) return '0 B';
	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}




// ============================================================================
// SELECT2 PARA AMBIENTES
// ============================================================================
function initSelect2Ambientes(ambientes, selectId) {
	const $select = $(`#${selectId}`);

	// Destruir select2 previo si existe
	if ($select.hasClass('select2-hidden-accessible')) {
		$select.select2('destroy');
	}

	// Preparar datos
	const data = ambientes.map(a => ({
		id: a.id,
		text: a.nombre
	}));

	// Inicializar Select2
	$select.select2({
		data: data,
		placeholder: 'Selecciona ambientes',
		allowClear: true,
		multiple: true,
		dropdownParent: $select.closest('.modal')
	});
}

// ============================================================================
// OBTENER DATOS MODAL AMBIENTES
// ============================================================================
export function getDataAsignacionAmbientes(usuarioId) {
	currentUsuarioId = usuarioId;

	// Obtener ambientes seleccionados
	const ambientesIds = $('#selectAmbientes').val() || [];

	if (ambientesIds.length === 0) {
		window.showNotification('Selecciona al menos un ambiente', 'warning');
		return null;
	}

	// Obtener datos del formulario
	const titulo = document.getElementById('tituloAmbientes').value.trim();
	const descripcion = document.getElementById('descripcionAmbientes').value.trim();

	// Obtener etiquetas desde Tokenfield (opcional)
	const etiquetasElement = document.getElementById('etiquetasAmbientes');
	const tokenfield = etiquetasElement?.tokenfield;
	const etiquetasArray = tokenfield ? tokenfield.getItems().map(item => item.name) : [];
	const etiquetasInput = etiquetasArray.join(',');

	// Validaciones
	if (!titulo) {
		window.showNotification('El título es requerido', 'warning');
		return null;
	}

	// Obtener fotos seleccionadas
	const fotosSeleccionadas = fotosEnMemoria
		.filter(f => f.seleccionada)
		.map(f => ({
			nombreArchivo: f.nombre,
			base64Data: f.base64Data,
			tamañoBytes: f.tamaño,
			subidaPorUsuarioId: usuarioId
		}));

	if (fotosSeleccionadas.length === 0) {
		window.showNotification('No hay fotos seleccionadas', 'warning');
		return null;
	}

	// Construir request
	return {
		ambientesIds: ambientesIds.map(id => parseInt(id)),
		estudiantesIds: [], // Se llenarán en el servidor
		actividadId: null,
		titulo: titulo,
		descripcion: descripcion,
		etiquetas: etiquetasInput, // Las etiquetas del usuario (se añadirá "privada" en el servidor)
		ambienteId: 0,
		imagenes: fotosSeleccionadas
	};
}

// ============================================================================
// OBTENER FOTOS SELECCIONADAS (para Blazor)
// ============================================================================
export function getFotosSeleccionadas(usuarioId) {
	const fotosSeleccionadas = fotosEnMemoria
		.filter(f => f.seleccionada)
		.map(f => ({
			nombreArchivo: f.nombre,
			base64Data: f.base64Data,
			tamañoBytes: f.tamaño,
			subidaPorUsuarioId: usuarioId
		}));
	
	return fotosSeleccionadas;
}

// ============================================================================
// INICIALIZAR TOKENFIELD DESDE BLAZOR
// ============================================================================
export function initTokenfieldModal(inputId) {
	initTokenfieldEtiquetas(inputId);
}


// OBTENER DATOS DEL FORMULARIO (sin IDs de select2)
export function getFormDataEstudiantes(usuarioId) {
	const titulo = document.getElementById('tituloEstudiantes')?.value.trim() || '';
	const descripcion = document.getElementById('descripcionEstudiantes')?.value.trim() || '';
	const etiquetasElement = document.getElementById('etiquetasEstudiantes');
	const tokenfield = etiquetasElement?.tokenfield;
	const etiquetasArray = tokenfield ? tokenfield.getItems().map(item => item.name) : [];
	const etiquetas = etiquetasArray.join(',');
	const imagenes = getFotosSeleccionadas(usuarioId);
	return { titulo, descripcion, etiquetas, imagenes };
}

export function getFormDataAmbientes(usuarioId) {
	const titulo = document.getElementById('tituloAmbientes')?.value.trim() || '';
	const descripcion = document.getElementById('descripcionAmbientes')?.value.trim() || '';
	const etiquetasElement = document.getElementById('etiquetasAmbientes');
	const tokenfield = etiquetasElement?.tokenfield;
	const etiquetasArray = tokenfield ? tokenfield.getItems().map(item => item.name) : [];
	const etiquetas = etiquetasArray.join(',');
	const imagenes = getFotosSeleccionadas(usuarioId);
	return { titulo, descripcion, etiquetas, imagenes };
}

